import { useId } from 'react';
import { cn } from '../../lib/utils';
import { doneState, type LiveStep } from './useTurn';

/**
 * What Hana did, step by step (mockup `.trail`): a violet rule on the left — the mockup's step rule, not a selection
 * marker — with ✓ for a finished step, ◌ while one runs, ✕ for a failed one and a warn-coloured ? for an outside call
 * that may or may not have gone out (EXT_UNKNOWN, G3 F2; its label keeps the normal colour). Only the step's label
 * shows, never its error code. A finished turn folds its trail into "✓ n bước", which opens it again.
 *
 * Whether a folded trail is open is the caller's (`expanded`, `onToggle`): the live turn's trail and the stored
 * message that takes its place are two components, and a trail the person opened stays open across that swap.
 */
export function Trail({ steps, folded, still, expanded, onToggle }: {
  steps: readonly LiveStep[]; folded: boolean; still: boolean; expanded: boolean; onToggle: () => void;
}) {
  const listId = useId();
  if (steps.length === 0) return null;
  const rows = (
    <ul id={listId} className="flex flex-col gap-0.5">
      {steps.map((s) => (
        <li key={s.callId} className="flex items-baseline gap-1.5">
          {s.state === 'ok' && <span aria-hidden="true" className="text-[hsl(var(--h-success))]">✓</span>}
          {s.state === 'running' && (
            <span aria-hidden="true"
              className={cn('text-[hsl(var(--h-icon-violet))]', !still && 'animate-pulse motion-reduce:animate-none')}>◌</span>
          )}
          {s.state === 'failed' && <span aria-hidden="true" className="text-[hsl(var(--h-error))]">✕</span>}
          {s.state === 'unknown' && <span aria-hidden="true" className="text-[hsl(var(--h-warning-fg))]">?</span>}
          <span className={cn(s.state === 'failed' && 'text-[hsl(var(--h-error))]')}>{s.label}</span>
        </li>
      ))}
    </ul>
  );
  return (
    <div data-trail=""
      className="ml-1.5 border-l-2 border-[hsl(var(--h-icon-bg-violet))] pl-2 text-xs text-[hsl(var(--h-muted-foreground))]">
      {folded ? (
        <>
          <button type="button" aria-expanded={expanded} aria-controls={expanded ? listId : undefined}
            onClick={onToggle}
            className="rounded px-0.5 text-left hover:text-[hsl(var(--h-card-foreground))]">
            <span className="text-[hsl(var(--h-success))]">✓</span>{` ${steps.length} bước`}
          </button>
          {expanded && <div className="mt-0.5">{rows}</div>}
        </>
      ) : rows}
    </div>
  );
}

/** The key a trail's open state is kept under: its first call, the same on the live turn and its stored message. */
export const trailKey = (steps: readonly { callId: string }[]): string | null => steps[0]?.callId ?? null;

export const stepsOf = (steps: readonly { callId: string; label: string; ok: boolean; errorCode?: string }[]):
  LiveStep[] => steps.map((s) => ({ callId: s.callId, label: s.label, state: doneState(s) }));
